'use client';

import { usePathname } from 'next/navigation';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getAppInsights } from '../utils/appInsights';
import { identifyUser } from '../utils/clarity';
import { postMilestone } from '../services/milestoneService';
import { ROUTE_FEATURE_MAP, EVENT_TYPES } from '../lib/analytics/milestones';
export default function AppRouteTracker({
  clarityReady
}) {
  const pathname = usePathname();
  const authUser = useSelector(state => state.auth?.authUser);
  useEffect(() => {
    const appInsights = getAppInsights();
    if (appInsights) {
      // Track route change with timing
      const startTime = performance.now();

      // Track the page view with custom properties
      appInsights.trackPageView({
        name: pathname,
        uri: window.location.href,
        properties: {
          route: pathname,
          timestamp: new Date().toISOString()
        },
        measurements: {
          navigationStart: startTime
        }
      });

      // Optional: Track route change as custom event
      appInsights.trackEvent({
        name: 'RouteChange',
        properties: {
          from: document.referrer || 'direct',
          to: pathname,
          timestamp: new Date().toISOString()
        }
      });
    }

    // Milestone: page_visit for activation-relevant routes
    const feature = ROUTE_FEATURE_MAP[pathname];
    if (feature) {
      postMilestone(feature, EVENT_TYPES.PAGE_VISIT);
    }
  }, [pathname]);

  // Robustly identify user, retry if Clarity not yet loaded
  useEffect(() => {
    const storeId = authUser?.store?.id;
    if (!storeId) return;

    // Function to check if Clarity is truly ready (global function exists)
    const isClarityReady = () => typeof window.clarity === 'function';

    // Attempt identification
    const tryIdentify = () => {
      if (isClarityReady()) {
        identifyUser(storeId);
        return true;
      }
      return false;
    };

    // 1. Try immediately
    if (tryIdentify()) return;

    // 2. Poll for it (since script might be async loading)
    let attempts = 0;
    const interval = setInterval(() => {
      attempts++;
      if (tryIdentify() || attempts > 20) {
        // Retry for 10 seconds (20 * 500ms)
        clearInterval(interval);
      }
    }, 500);
    return () => clearInterval(interval);
  }, [authUser?.store?.id]); // Removed clarityReady dependency to rely on self-check

  return null; // This component renders nothing
}